/* eslint-disable no-unused-vars */
import axios from "axios";
import { clearLocalStorage, getAuthToken, getLocal, setLocal } from "../helper/storage";
import { errorToast } from "../util/Toast/toast";

// eslint-disable-next-line no-undef
const _baseURL = process.env.REACT_APP_API_BASE_URL;

export const axiosInstance = axios.create({
  baseURL: _baseURL,
  headers: {
    'ngrok-skip-browser-warning': true,
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }
})

// Request interceptor (optional)
axiosInstance.interceptors.request.use(
  (config) => {
    const _token = getAuthToken()
    if (_token) config.headers.Authorization = `Bearer ${_token}`
    return config
  },
  (error) => Promise.reject(error)
);

// Flag to prevent multiple refresh token calls
let isRefreshing = false;
let failedRequestsQueue= [];

// Function to refresh access token
const refreshAccessToken = async () => {
  try {
    const refreshToken = getLocal("refrteshToken");
    if (!refreshToken) throw new Error("No refresh token available");

    const response = await axios.get(`${_baseURL}/client/auth/update-access-token`, {
      headers: { Authorization: `Bearer ${refreshToken}` },
    });

    if (response?.data) {
      const newAccessToken = response.data.token;
      const newRefreshToken = response.data.refreshToken;

      // Store new tokens
      setLocal("authToken", newAccessToken);
      setLocal("refrteshToken", newRefreshToken);

      return newAccessToken;
    }
  } catch (error) {
    console.error("Failed to refresh token", error);
    clearLocalStorage();
    window.location.href = "/login";
    throw error;
  }
};

// Response Interceptor: Handle 401 & Refresh Token
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    if (response?.status === 401 && !config._retry) {
      config._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        })
          .then((token) => {
            config.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(config);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        failedRequestsQueue.forEach((request) => request.resolve(newToken));
        failedRequestsQueue = [];
        config.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(config);
      } catch (refreshError) {
        failedRequestsQueue.forEach((request) => request.reject(refreshError));
        failedRequestsQueue = [];
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (response?.status === 403) {
      errorToast(response?.data?.status_message || `Something went wrong`);
    }
    if (response?.status === 500) {
      errorToast(response?.data?.status_message || `Something went wrong`);
    }
    return Promise.reject(response);
  }
);
